/**
 * Deep-link bridge: tender / award / contract → Pursuit workspace (opportunityId).
 */
import React, { useEffect, useState } from 'react';
import { Alert, Spin, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  pursuitPath,
  resolvePursuitOpportunityId,
} from '@/components/epc/commercial-ux/resolvePursuitOpportunityId';

const { Text } = Typography;

const ALLOWED = new Set(['epc_tender', 'epc_award', 'epc_contract']);

const PursuitResolveBridgePage: React.FC = () => {
  const { entityType = '', entityId = '' } = useParams<{
    entityType: string;
    entityId: string;
  }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ALLOWED.has(entityType) || !entityId) {
        setError('Unsupported commercial entity for pursuit resolve.');
        return;
      }
      try {
        const oppId = await resolvePursuitOpportunityId(
          entityType as 'epc_tender' | 'epc_award' | 'epc_contract',
          entityId
        );
        if (cancelled) return;
        if (!oppId) {
          setError('Could not resolve opportunity for this commercial document.');
          return;
        }
        navigate(pursuitPath(oppId), { replace: true });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Resolve failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, navigate]);

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="warning" showIcon message="Pursuit resolve" description={error} />
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          Open the opportunity from CRM, or ensure the document is linked via ECLE lineage.
        </Text>
      </div>
    );
  }

  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Spin tip="Opening pursuit…" />
    </div>
  );
};

export default PursuitResolveBridgePage;
